import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarDays, Clock, ChevronLeft, ChevronRight, Loader2, CheckCircle, Video, MapPin, Users, Globe, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function PublicBooking() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const isEmbed = searchParams.get('embed') === 'true';

    const [linkData, setLinkData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Calendar state
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);

    // Slots state
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);

    // Booking form
    const [step, setStep] = useState('calendar'); // calendar | form | confirmed
    const [formData, setFormData] = useState({ name: '', email: '', notes: '' });
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState(null);

    // Load booking link data
    useEffect(() => {
        const load = async () => {
            try {
                const res = await axios.get(`${API}/booking/public/${slug}`);
                setLinkData(res.data);
            } catch {
                setError('Booking page not found');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [slug]);

    // Load slots when date is selected
    useEffect(() => {
        if (!selectedDate) return;
        const loadSlots = async () => {
            setSlotsLoading(true);
            try {
                const dateStr = formatDateStr(selectedDate);
                const res = await axios.get(`${API}/booking/public/${slug}/slots?date=${dateStr}`);
                setSlots(res.data.slots || []);
            } catch { toast.error('Failed to load available times'); }
            finally { setSlotsLoading(false); }
        };
        loadSlots();
    }, [selectedDate, slug]);

    const formatDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const handleBook = async () => {
        if (!formData.name || !formData.email) return toast.error('Name and email are required');
        if (!selectedSlot) return;
        setSubmitting(true);
        try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            const res = await axios.post(`${API}/booking/public/${slug}`, {
                name: formData.name,
                email: formData.email,
                start: selectedSlot.start,
                end: selectedSlot.end,
                answers: { ...answers, notes: formData.notes },
                timezone: tz,
            });
            setConfirmation(res.data);
            setStep('confirmed');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Booking failed');
        } finally {
            setSubmitting(false);
        }
    };

    // Calendar generation helpers
    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const isDateAvailable = (date) => {
        if (!linkData) return false;
        const avail = linkData.availability || {};
        const allowed = avail.days || [0, 1, 2, 3, 4];
        // JS getDay: 0=Sun, convert to our 0=Mon
        const dayIdx = (date.getDay() + 6) % 7;
        if (!allowed.includes(dayIdx)) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date < today) return false;

        const maxDays = linkData.max_days_ahead || 60;
        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + maxDays);
        if (date > maxDate) return false;

        return true;
    };

    const navigateMonth = (dir) => {
        const d = new Date(currentMonth);
        d.setMonth(d.getMonth() + dir);
        setCurrentMonth(d);
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-8 h-8 text-stone-400 animate-spin" /></div>
    );

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-white">
            <div className="text-center"><CalendarDays className="w-12 h-12 text-stone-300 mx-auto mb-4" /><p className="text-stone-500">{error}</p></div>
        </div>
    );

    const brandColor = linkData?.color || '#1a1a1a';
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const calendarDays = [];
    for (let i = 0; i < firstDay; i++) calendarDays.push(null);
    for (let d = 1; d <= daysInMonth; d++) calendarDays.push(new Date(year, month, d));

    // Confirmed step
    if (step === 'confirmed' && confirmation) {
        return (
            <div className={`${isEmbed ? '' : 'min-h-screen'} flex items-center justify-center bg-white p-6`}>
                <div className="w-full max-w-md text-center" data-testid="booking-confirmed">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: brandColor + '15' }}>
                        <CheckCircle className="w-8 h-8" style={{ color: brandColor }} />
                    </div>
                    <h2 className="text-xl font-bold text-stone-900 mb-2">Meeting Scheduled!</h2>
                    <p className="text-sm text-stone-500 mb-6">{linkData.confirmation_message || 'Your meeting has been scheduled.'}</p>
                    <div className="bg-stone-50 rounded-xl p-4 text-left space-y-2 mb-4">
                        <p className="text-sm"><strong>What:</strong> {linkData.name}</p>
                        <p className="text-sm"><strong>When:</strong> {new Date(confirmation.start_time).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at {new Date(confirmation.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                        {confirmation.assigned_user_name && <p className="text-sm"><strong>With:</strong> {confirmation.assigned_user_name}</p>}
                        {confirmation.meet_link && (
                            <a href={confirmation.meet_link} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-sm font-medium rounded-lg text-white" style={{ backgroundColor: brandColor }}>
                                <Video className="w-4 h-4" /> {linkData.location === 'zoom' ? 'Join Zoom Meeting' : linkData.location === 'google_meet' ? 'Join Google Meet' : 'Join Meeting'}
                            </a>
                        )}
                    </div>
                    <p className="text-xs text-stone-400">A calendar invite has been sent to {confirmation.guest_email}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`${isEmbed ? '' : 'min-h-screen'} bg-white flex items-center justify-center p-4`}>
            <div className="w-full max-w-3xl" data-testid="public-booking-page">
                {/* Header */}
                <div className="text-center mb-6">
                    {linkData.org_name && <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-1">{linkData.org_name}</p>}
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{linkData.name}</h1>
                    {linkData.description && <p className="text-sm text-stone-500 mt-1 max-w-lg mx-auto">{linkData.description}</p>}
                    <div className="flex items-center justify-center gap-4 mt-3 text-xs text-stone-500">
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {linkData.duration} min</span>
                        {linkData.location === 'google_meet' && <span className="flex items-center gap-1"><Video className="w-3.5 h-3.5" /> Google Meet</span>}
                        {linkData.location === 'zoom' && <span className="flex items-center gap-1"><Video className="w-3.5 h-3.5" /> Zoom</span>}
                        {linkData.location === 'phone' && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Phone</span>}
                        {linkData.booking_type === 'round_robin' && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Team</span>}
                    </div>
                </div>

                <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden shadow-sm">
                    {step === 'calendar' ? (
                        <div className="md:flex">
                            {/* Calendar */}
                            <div className="flex-1 p-5 border-r border-stone-100">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-stone-800">{MONTH_NAMES[month]} {year}</h3>
                                    <div className="flex items-center gap-1">
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => navigateMonth(-1)}><ChevronLeft className="w-4 h-4" /></Button>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => navigateMonth(1)}><ChevronRight className="w-4 h-4" /></Button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-7 gap-1 mb-1">
                                    {DAY_NAMES.map(d => <div key={d} className="text-center text-xs font-medium text-stone-400 py-1">{d}</div>)}
                                </div>
                                <div className="grid grid-cols-7 gap-1">
                                    {calendarDays.map((day, i) => {
                                        if (!day) return <div key={`empty-${i}`} />;
                                        const available = isDateAvailable(day);
                                        const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
                                        const isToday = day.toDateString() === new Date().toDateString();
                                        return (
                                            <button key={day.getDate()} disabled={!available} onClick={() => { setSelectedDate(day); setSelectedSlot(null); }}
                                                className={`h-10 w-full rounded-lg text-sm font-medium transition-all ${isSelected ? 'text-white' : available ? 'text-stone-800 hover:bg-stone-100' : 'text-stone-300 cursor-not-allowed'} ${isToday && !isSelected ? 'ring-1 ring-stone-300' : ''}`}
                                                style={isSelected ? { backgroundColor: brandColor } : undefined}
                                                data-testid={`cal-day-${day.getDate()}`}>
                                                {day.getDate()}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Time Slots */}
                            <div className="w-64 p-5 flex flex-col">
                                {!selectedDate ? (
                                    <div className="flex-1 flex items-center justify-center text-stone-400 text-sm text-center">
                                        <div><CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>Select a date to see available times</p></div>
                                    </div>
                                ) : slotsLoading ? (
                                    <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
                                ) : (
                                    <>
                                        <p className="text-sm font-medium text-stone-700 mb-3">
                                            {selectedDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                        </p>
                                        <div className="flex-1 overflow-y-auto space-y-1.5 max-h-80">
                                            {slots.length === 0 ? (
                                                <p className="text-sm text-stone-400 text-center py-8">No available times</p>
                                            ) : slots.map((slot, i) => {
                                                const t = new Date(slot.start);
                                                const timeStr = t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                                const isSelected = selectedSlot?.start === slot.start;
                                                return (
                                                    <button key={i} onClick={() => setSelectedSlot(slot)}
                                                        className={`w-full py-2.5 px-3 rounded-lg text-sm font-medium border transition-all ${isSelected ? 'text-white border-transparent' : 'text-stone-700 border-stone-200 hover:border-stone-400'}`}
                                                        style={isSelected ? { backgroundColor: brandColor, borderColor: brandColor } : undefined}
                                                        data-testid={`slot-${i}`}>
                                                        {timeStr}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {selectedSlot && (
                                            <Button className="mt-3 w-full text-white" style={{ backgroundColor: brandColor }}
                                                onClick={() => setStep('form')} data-testid="continue-to-form">
                                                Continue
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    ) : step === 'form' ? (
                        <div className="p-6 max-w-md mx-auto" data-testid="booking-form">
                            <button onClick={() => setStep('calendar')} className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 mb-4">
                                <ChevronLeft className="w-4 h-4" /> Back
                            </button>
                            <div className="bg-stone-50 rounded-lg p-3 mb-5 text-sm">
                                <p className="font-medium text-stone-800">{linkData.name}</p>
                                <p className="text-stone-500">{selectedDate?.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })} at {new Date(selectedSlot?.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                <p className="text-stone-400">{linkData.duration} min{linkData.location === 'google_meet' ? ' · Google Meet' : linkData.location === 'zoom' ? ' · Zoom' : ''}</p>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-xs font-semibold text-stone-500 uppercase">Name *</Label>
                                    <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Your full name" className="mt-1" data-testid="booking-name" />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-stone-500 uppercase">Email *</Label>
                                    <Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="you@company.com" className="mt-1" data-testid="booking-email" />
                                </div>
                                {(linkData.custom_questions || []).map((q, i) => (
                                    <div key={i}>
                                        <Label className="text-xs font-semibold text-stone-500 uppercase">{q.label} {q.required && '*'}</Label>
                                        <Input value={answers[q.label] || ''} onChange={e => setAnswers({ ...answers, [q.label]: e.target.value })} className="mt-1" data-testid={`custom-q-${i}`} />
                                    </div>
                                ))}
                                <div>
                                    <Label className="text-xs font-semibold text-stone-500 uppercase">Notes (optional)</Label>
                                    <Textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Anything else we should know?" rows={2} className="mt-1" data-testid="booking-notes" />
                                </div>
                            </div>
                            <Button className="w-full mt-5 text-white" style={{ backgroundColor: brandColor }} onClick={handleBook} disabled={submitting} data-testid="confirm-booking-btn">
                                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                                Confirm Booking
                            </Button>
                        </div>
                    ) : null}
                </div>

                {/* Footer */}
                {!isEmbed && (
                    <p className="text-center text-xs text-stone-400 mt-6">
                        Powered by <span className="font-semibold text-stone-600">NurtureCRM</span>
                    </p>
                )}
            </div>
        </div>
    );
}
